/**
 * Audio System Type Definitions
 * TypeScript types for the sound system framework
 */

/**
 * Sound category enum for volume control and organization
 */
export enum SoundCategory {
  MUSIC = 'music',
  SFX = 'sfx',
  AMBIENT = 'ambient',
  UI = 'ui',
  VOICE = 'voice',
}

/**
 * Options for playing a sound
 */
export interface SoundOptions {
  /** Sound category (defaults to SFX) */
  category?: SoundCategory;
  /** Whether to loop the sound (defaults to false) */
  loop?: boolean;
  /** Volume multiplier (0.0 to 1.0, defaults to 1.0) */
  volume?: number;
  /** Stereo pan position (-1.0 = left, 0.0 = center, 1.0 = right) */
  pan?: number;
  /** Start playback at specific time in seconds (defaults to 0) */
  startTime?: number;
  /** Fade in duration in milliseconds (defaults to 0) */
  fadeIn?: number;
  /** Fade out duration in milliseconds when stopping (defaults to 0) */
  fadeOut?: number;
}

/**
 * Internal sound instance tracking
 */
export interface SoundInstance {
  /** Unique ID for this sound instance */
  id: string;
  /** Audio source node */
  source: AudioBufferSourceNode;
  /** Gain node for volume control */
  gainNode: GainNode;
  /** Panner node for stereo positioning */
  panNode: StereoPannerNode;
  /** Sound category */
  category: SoundCategory;
  /** Whether this sound is looping */
  isLooping: boolean;
  /** Time when playback started */
  startedAt: number;
  /** Whether the sound is currently paused */
  isPaused: boolean;
  /** Pause offset for resume functionality */
  pauseOffset: number;
  /** Original audio buffer */
  buffer: AudioBuffer;
  /** File path for re-creation */
  filePath: string;
}

/**
 * Audio preferences stored in localStorage
 */
export interface AudioPreferences {
  /** Master volume (0.0 to 1.0) */
  masterVolume: number;
  /** Whether all audio is muted */
  isMuted: boolean;
  /** Category-specific volumes (0.0 to 1.0) */
  categoryVolumes: {
    [SoundCategory.MUSIC]: number;
    [SoundCategory.SFX]: number;
    [SoundCategory.AMBIENT]: number;
    [SoundCategory.UI]: number;
    [SoundCategory.VOICE]: number;
  };
}

/**
 * Sound playback state for store
 */
export interface SoundState {
  /** Currently playing sounds */
  activeSounds: Map<string, SoundInstance>;
  /** Master volume (0.0 to 1.0) */
  masterVolume: number;
  /** Is all audio muted? */
  isMuted: boolean;
  /** Category volumes (0.0 to 1.0) */
  categoryVolumes: Map<SoundCategory, number>;
  /** Is audio system initialized? */
  isInitialized: boolean;
  /** Is AudioContext suspended (requires user interaction)? */
  isSuspended: boolean;
}
